import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApprovalFormTable,
  ApprovalTableAddRowButton,
  ApprovalTableCellInput,
  ApprovalTableCurrencySelect,
  ApprovalTableRow,
  ApprovalTableRowDeleteButton,
} from '@/components/Approval/@components/ApprovalFormTable';
import { useDialog } from '@/hooks/global-tools';
import PlusIcon from '@/public/icons/approval/plus.svg';
import {
  createDetailRow,
  CURRENCY_OPTIONS,
  formatAmountInput,
  formatQuantityInput,
  type ExpenseDetailRow,
} from '@/utils/approval/expensePreApproval';

/** edit = 입력칸·선택칸 수정(계산 무효) / structure = 행 추가·삭제(계산 상태 유지) — 레거시 1807행 input·change */
export type ItemTableChangeKind = 'edit' | 'structure';

type ItemTableProps = {
  rows: ExpenseDetailRow[];
  onChange: (rows: ExpenseDetailRow[], kind: ItemTableChangeKind) => void;
};

const hasAnyValue = (row: ExpenseDetailRow) =>
  [row.itemName, row.quantity, row.unitPrice, row.netAmount, row.remark].some(
    (v) => v.trim() !== '',
  );

/** 품목 표 — 행 추가 무제한, 1행 남으면 삭제 불가, 입력값 있는 행만 삭제 확인(레거시) */
const ItemTable = ({ rows, onChange }: ItemTableProps) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);

  const patch = (id: string, next: Partial<ExpenseDetailRow>) =>
    onChange(
      rows.map((r) => (r.id === id ? { ...r, ...next } : r)),
      'edit',
    );

  const remove = (row: ExpenseDetailRow) => {
    const doRemove = () =>
      onChange(
        rows.filter((r) => r.id !== row.id),
        'structure',
      );
    if (!hasAnyValue(row)) {
      doRemove();
      return;
    }
    dialog.show({
      message: t(
        '해당 항목을 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.',
      ),
      confirmButtonText: t('삭제'),
      cancelButtonText: t('취소'),
      onConfirm: doRemove,
    });
  };

  const canDelete = rows.length > 1;

  return (
    <div className="additional-table">
      <ApprovalFormTable editable className="expense-item-table">
        <colgroup>
          <col />
          <col style={{ width: 84 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 96 }} />
          <col />
          <col style={{ width: 32 }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t('품목')}</th>
            <th>{t('수량')}</th>
            <th>{t('단가')}</th>
            <th>{t('공급가액')}</th>
            <th>{t('통화')}</th>
            <th>{t('비고')}</th>
            <th aria-label={t('삭제')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ApprovalTableRow
              key={row.id}
              deleteHover={canDelete && hoverRowId === row.id}
              data-row-id={row.id}
            >
              <td>
                <ApprovalTableCellInput
                  name="itemName"
                  value={row.itemName}
                  onChange={(e) => patch(row.id, { itemName: e.target.value })}
                />
              </td>
              <td>
                <ApprovalTableCellInput
                  name="quantity"
                  className="align-center"
                  inputProps={{ inputMode: 'numeric' }}
                  value={row.quantity}
                  onChange={(e) =>
                    patch(row.id, {
                      quantity: formatQuantityInput(e.target.value),
                    })
                  }
                />
              </td>
              <td>
                <ApprovalTableCellInput
                  name="unitPrice"
                  className="align-right"
                  inputProps={{ inputMode: 'decimal' }}
                  value={row.unitPrice}
                  onChange={(e) =>
                    patch(row.id, {
                      unitPrice: formatAmountInput(e.target.value),
                    })
                  }
                />
              </td>
              <td>
                <ApprovalTableCellInput
                  name="netAmount"
                  className="align-right"
                  inputProps={{ inputMode: 'decimal' }}
                  value={row.netAmount}
                  onChange={(e) =>
                    patch(row.id, {
                      netAmount: formatAmountInput(e.target.value),
                    })
                  }
                />
              </td>
              <td>
                <ApprovalTableCurrencySelect
                  value={row.currency}
                  options={CURRENCY_OPTIONS}
                  onChange={(currency) => patch(row.id, { currency })}
                />
              </td>
              <td>
                <ApprovalTableCellInput
                  name="remark"
                  value={row.remark}
                  onChange={(e) => patch(row.id, { remark: e.target.value })}
                />
              </td>
              <td className="align-center">
                <ApprovalTableRowDeleteButton
                  type="button"
                  aria-label={t('삭제')}
                  disabled={!canDelete}
                  onMouseEnter={() => setHoverRowId(row.id)}
                  onMouseLeave={() => setHoverRowId(null)}
                  onClick={() => remove(row)}
                />
              </td>
            </ApprovalTableRow>
          ))}
        </tbody>
      </ApprovalFormTable>
      <ApprovalTableAddRowButton
        type="button"
        onClick={() => onChange([...rows, createDetailRow()], 'structure')}
      >
        <PlusIcon />
        {t('항목 추가')}
      </ApprovalTableAddRowButton>
    </div>
  );
};

export default ItemTable;
